import type { ReactNode } from 'react';
import { formLabels } from '../types';
import type { DashboardDetail } from '../dashboardTypes';
import { DASH, formatDay, formatInstant, formatMoney } from '../format';
import { issueDetailsFor, methodLabel } from './statusCopy';
import { SimulationControls } from './SimulationControls';

/** A value, or a visible dash with a screen-reader note when the server has no source for it yet. Never an invented value. */
function Value({ value, label }: { value: ReactNode | null | undefined; label?: string }) {
  if (value === null || value === undefined || value === '') {
    return <span title={label ? `${label}: not available yet` : undefined}>{DASH}{label && <span className="tpd-visually-hidden"> {label} not available yet</span>}</span>;
  }
  return <>{value}</>;
}

function Item({ label, children }: { label: string; children: ReactNode }) {
  return <div className="tpd-grid__item"><dt>{label}</dt><dd>{children}</dd></div>;
}

/**
 * Payment Details tab (Figma S10, S24, S34, S40). Every field is the server's. Fields the design shows that have no source yet (masked bank account, confirmation
 * number, payment origin, Auto Tax Payment, related filing and payroll run, Process No Later Than) are a dash with a "not available yet" note. The amount breakdown
 * and readiness checks have no source and are not drawn; the payment amount is shown as the total.
 */
export function DetailsTab({ detail, showSimulation, simulationKey, onSimulationChanged, onViewRecord }: {
  detail: DashboardDetail;
  showSimulation: boolean;
  simulationKey: number;
  onSimulationChanged: () => void;
  onViewRecord: () => void;
}) {
  const { row, outsideRecord, insufficientFunds } = detail;
  const issue = issueDetailsFor(detail);
  const form = formLabels[row.formCode] ?? row.formCode;
  return (
    <div className="tpd-tabbody" data-testid="tab-details">
      <dl className="tpd-grid">
        <Item label="Tax Payment ID"><span className="tpd-mono">{row.id}</span></Item>
        <Item label="Tax Period">{formatDay(row.periodEndDate)}</Item>
        <Item label="Tax Payment Frequency"><span className="tpd-chip">{row.frequency}</span></Item>
        <Item label="Tax Type / Form">{form}</Item>
        <Item label="Agency / Jurisdiction">{row.agency} — {row.jurisdiction}</Item>
        <Item label="Payment Due Date (legal)">{formatDay(row.dueDate)}</Item>
        <Item label="Process No Later Than"><Value value={row.processNoLaterThanUtc ? formatInstant(row.processNoLaterThanUtc) : null} label="Process No Later Than" /></Item>
        <Item label="Amount">{formatMoney(row.amount)}</Item>
        <Item label="Payment Initiated / Submitted"><Value value={row.initiatedAtUtc ? formatInstant(row.initiatedAtUtc) : null} /></Item>
        <Item label="Date Paid"><Value value={row.datePaid ? formatDay(row.datePaid) : null} /></Item>
        <Item label="Payment Method"><Value value={outsideRecord && outsideRecord.status === 'Active' ? methodLabel(outsideRecord.method) : null} label="Payment Method" /></Item>
        <Item label="Masked Bank Account"><Value value={null} label="Masked Bank Account" /></Item>
        <Item label="Confirmation Number"><Value value={null} label="Confirmation Number" /></Item>
        <Item label="Agency Reference Number"><Value value={outsideRecord && outsideRecord.status === 'Active' ? outsideRecord.referenceNumber : null} /></Item>
        <Item label="Payment Partner"><Value value={row.paymentPartner} label="Payment Partner" /></Item>
        <Item label="Payment Origin"><Value value={null} label="Payment Origin" /></Item>
        <Item label="Auto Tax Payment"><Value value={null} label="Auto Tax Payment" /></Item>
        <Item label="Related Filing"><Value value={null} label="Related Filing" /></Item>
        <Item label="Related Payroll Run"><Value value={null} label="Related Payroll Run" /></Item>
      </dl>

      <section className="tpd-section" aria-labelledby="tpd-breakdown">
        <h4 id="tpd-breakdown" className="tpd-subhead">Amount Breakdown</h4>
        <div className="tpd-total" data-testid="amount-total"><span>Total</span><strong>{formatMoney(row.amount)}</strong></div>
        <p className="tpd-muted">Line items and readiness checks are not available yet.</p>
      </section>

      {issue && (
        <section className="tpd-section" aria-labelledby="tpd-issue" data-testid="issue-details">
          <h4 id="tpd-issue" className="tpd-subhead">{row.status === 'NeedsReview' ? 'Review Details' : 'Failure Details'}</h4>
          <div className="tpd-issue">
            <p className="tpd-issue__title">{issue.title}</p>
            <p>{issue.explanation}</p>
            <p><span className="tpd-muted">Required next action</span><br />{issue.nextAction}</p>
          </div>
        </section>
      )}

      {outsideRecord && (
        <section className="tpd-section" aria-labelledby="tpd-outside" data-testid="outside-summary">
          <h4 id="tpd-outside" className="tpd-subhead">Outside payment record</h4>
          <p>
            {outsideRecord.status === 'Voided' ? 'Voided record' : 'Recorded'}: {formatMoney(outsideRecord.amount)} on {formatDay(outsideRecord.paymentDate)} by {methodLabel(outsideRecord.method)}.
            {' '}{outsideRecord.documents.length} {outsideRecord.documents.length === 1 ? 'file' : 'files'} attached.
          </p>
          <button type="button" className="tpd-link" onClick={onViewRecord}>View Payment Record</button>
        </section>
      )}

      {insufficientFunds && (
        <section className="tpd-section" aria-labelledby="tpd-if" data-testid="insufficient-summary">
          <h4 id="tpd-if" className="tpd-subhead">Insufficient funds evidence</h4>
          <dl className="tpd-grid">
            <Item label="Source">{insufficientFunds.source === 'KotapayManual' ? 'Kotapay (recorded by hand)' : insufficientFunds.source}</Item>
            <Item label="Date identified">{formatDay(insufficientFunds.dateIdentified)}</Item>
            <Item label="Reference">{insufficientFunds.reasonReference}</Item>
            <Item label="Recorded by">{insufficientFunds.recordedByRole}</Item>
          </dl>
          {insufficientFunds.internalNote && <p><span className="tpd-muted">Internal note</span><br />{insufficientFunds.internalNote}</p>}
        </section>
      )}

      {showSimulation && <SimulationControls paymentId={row.id} refreshKey={simulationKey} onChanged={onSimulationChanged} />}
    </div>
  );
}
